import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Loader2, Mail, Eye, MousePointerClick, AlertTriangle,
  CheckCircle2, UserMinus, TrendingUp, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";

const API = `${window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const RANGE_OPTIONS = [
  { id: 7, label: "7d" },
  { id: 30, label: "30d" },
  { id: 90, label: "90d" },
  { id: 365, label: "1y" },
];

function auth() {
  return { Authorization: `Bearer ${localStorage.getItem("session_token")}` };
}

// Industry benchmarks for small-business B2C email — used to color-code
// the rates so the admin knows at a glance if a step is healthy or
// underperforming. These aren't hard cutoffs, just gut-check ranges.
function rateTone(rate, healthy, warning) {
  if (rate >= healthy) return "text-green-700";
  if (rate >= warning) return "text-amber-700";
  return "text-zinc-500";
}

export default function EmailDripReporting() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [backfilling, setBackfilling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/email-drip/stats?days=${days}`, { headers: auth() });
      setStats(res.data);
    } catch (err) {
      toast.error("Failed to load drip stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const runBackfill = async () => {
    if (!window.confirm(`Enrol every drip-eligible lead from the last ${days} days who isn't already in the sequence?`)) return;
    setBackfilling(true);
    try {
      const res = await axios.post(`${API}/email-drip/backfill?days=${days}`, {}, { headers: auth() });
      toast.success(`Enrolled ${res.data.enrolled} leads (${res.data.skipped_already_enrolled_or_optout} were already in / opted out)`);
      load();
    } catch (err) {
      toast.error("Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  const t = stats?.totals || {};
  const f = stats?.funnel || {};
  const steps = stats?.per_step || [];
  const bounceRate = t.sent ? Math.round((t.bounced / t.sent) * 1000) / 10 : 0;
  const unsubRate = t.sent ? Math.round((t.unsubscribed / t.sent) * 1000) / 10 : 0;
  const bouncesHigh = bounceRate >= 5;  // Resend warns >5%
  const unsubHigh = unsubRate >= 1;     // industry caution at ~1%

  // Tracking-broken heuristic: we've delivered emails but haven't received
  // a SINGLE open or click event. Almost always means Resend has open/click
  // tracking disabled on the domain OR the webhook isn't subscribed to
  // those event types. Genuine 0% opens across hundreds of sends is
  // vanishingly rare, so flag it loudly.
  const trackingBroken =
    (t.sent || 0) >= 20 && (t.opened || 0) === 0 && (t.clicked || 0) === 0;

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-6 pb-32">
        <button
          onClick={() => navigate("/settings")}
          className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-6"
          data-testid="back-to-settings"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Settings</span>
        </button>

        <div className="mb-6 flex items-start justify-between gap-3">
          <div>
            <h1 className="font-barlow text-3xl font-bold text-zinc-950 uppercase">
              Email Drip Reporting
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              5-step lead nurture sequence performance. Open / click / book conversion per step.
            </p>
            <button
              onClick={() => navigate("/settings/email-drip-templates")}
              data-testid="edit-templates-link"
              className="mt-2 text-xs font-semibold text-rose-700 hover:text-rose-900 underline"
            >
              Edit copy + send test emails →
            </button>
          </div>
          <div className="flex gap-1 bg-zinc-100 p-1 rounded-lg text-xs flex-shrink-0">
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setDays(opt.id)}
                data-testid={`range-${opt.id}`}
                className={`px-2.5 py-1 rounded-md font-medium transition ${
                  days === opt.id ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-800"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tracking diagnostic — fire only when opens/clicks are BOTH zero
            across a meaningful send volume. Points the admin at the exact
            two Resend switches that need to be on. */}
        {trackingBroken && (
          <div
            className="mb-6 rounded-xl border-2 border-amber-300 bg-amber-50 p-4"
            data-testid="tracking-broken-banner"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-bold text-amber-900">
                  Open + click tracking looks disabled
                </p>
                <p className="text-amber-800 mt-1">
                  We sent <strong>{t.sent}</strong> emails in the last {days} days but
                  received <strong>zero</strong> open/click events from Resend. That&apos;s
                  almost never real behaviour — tracking is likely turned off. Fix it in
                  two places:
                </p>
                <ol className="list-decimal list-inside text-amber-800 mt-2 space-y-1">
                  <li>
                    Resend dashboard →{" "}
                    <a
                      href="https://resend.com/domains"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-semibold"
                    >
                      Domains
                    </a>{" "}
                    → select your sending domain → toggle <em>Open Tracking</em> and{" "}
                    <em>Click Tracking</em> to <strong>ON</strong>.
                  </li>
                  <li>
                    Resend dashboard →{" "}
                    <a
                      href="https://resend.com/webhooks"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-semibold"
                    >
                      Webhooks
                    </a>{" "}
                    → your endpoint → subscribe to <code className="bg-amber-100 px-1 rounded">email.opened</code>{" "}
                    and <code className="bg-amber-100 px-1 rounded">email.clicked</code>.
                  </li>
                </ol>
                <p className="text-amber-800 mt-2 text-xs">
                  Sends and bounces work fine — those events are already flowing. Only opens/clicks are missing.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Funnel — Enrolled → Booked */}
        <div className="bg-white rounded-xl border-2 border-zinc-100 p-5 mb-6" data-testid="funnel-card">
          <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wide mb-3 flex items-center gap-2">
            <TrendingUp className="w-3.5 h-3.5" /> Lead Funnel
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <FunnelStage label="Enrolled" value={f.enrolled || 0} tone="zinc" />
            <FunnelStage
              label="Booked / Converted"
              value={f.booked_or_converted || 0}
              sub={`${f.booking_rate || 0}% of enrolled`}
              tone="green"
            />
            <FunnelStage
              label="Unsubscribed"
              value={t.unsubscribed || 0}
              sub={`${unsubRate}% of sent${unsubHigh ? " ⚠" : ""}`}
              tone={unsubHigh ? "amber" : "zinc"}
            />
          </div>
        </div>

        {/* KPI strip — overall engagement */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <KPI icon={<Mail className="w-4 h-4" />} label="Sent" value={t.sent || 0} bg="bg-zinc-100" fg="text-zinc-700" testid="kpi-sent" />
          <KPI icon={<Eye className="w-4 h-4" />} label="Opened" value={t.opened || 0} sub={`${t.open_rate || 0}% open rate`} bg="bg-blue-100" fg="text-blue-700" testid="kpi-opened" />
          <KPI icon={<MousePointerClick className="w-4 h-4" />} label="Clicked" value={t.clicked || 0} sub={`${t.click_rate || 0}% CTR`} bg="bg-indigo-100" fg="text-indigo-700" testid="kpi-clicked" />
          <KPI
            icon={<AlertTriangle className="w-4 h-4" />}
            label="Bounced"
            value={t.bounced || 0}
            sub={`${bounceRate}%${bouncesHigh ? " ⚠ high" : ""}`}
            bg={bouncesHigh ? "bg-red-100" : "bg-zinc-100"}
            fg={bouncesHigh ? "text-red-700" : "text-zinc-700"}
            testid="kpi-bounced"
          />
        </div>

        {/* Per-step table */}
        <div className="bg-white rounded-xl border-2 border-zinc-100 mb-6">
          <div className="px-5 py-3 border-b text-[11px] uppercase tracking-wide text-zinc-400 font-semibold flex items-center gap-2">
            <CheckCircle2 className="w-3.5 h-3.5" /> Engagement by Step
          </div>
          {steps.map((s) => (
            <div key={s.key} className="px-5 py-4 border-b last:border-b-0" data-testid={`step-row-${s.step}`}>
              <div className="flex items-baseline justify-between gap-2 mb-2">
                <div className="flex items-baseline gap-2 min-w-0">
                  <span className="text-[10px] font-bold text-zinc-400 uppercase">Step {s.step}</span>
                  <span className="text-sm text-zinc-900 truncate font-medium">{s.subject}</span>
                </div>
                <span className="text-[10px] text-zinc-400 font-mono whitespace-nowrap">
                  +{Math.round((s.offset_hours / 24) * 10) / 10}d
                </span>
              </div>
              <div className="grid grid-cols-4 gap-2 text-xs">
                <div>
                  <div className="text-[10px] text-zinc-400 uppercase">Sent</div>
                  <div className="text-base font-semibold text-zinc-900">{s.sent}</div>
                </div>
                <div>
                  <div className="text-[10px] text-zinc-400 uppercase">Opened</div>
                  <div className={`text-base font-semibold ${rateTone(s.open_rate, 25, 15)}`}>
                    {s.opened}
                    <span className="text-[11px] font-normal ml-1">{s.open_rate}%</span>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-zinc-400 uppercase">Clicked</div>
                  <div className={`text-base font-semibold ${rateTone(s.click_rate, 5, 2)}`}>
                    {s.clicked}
                    <span className="text-[11px] font-normal ml-1">{s.click_rate}%</span>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-zinc-400 uppercase">Bounced</div>
                  <div className="text-base font-semibold text-zinc-500">{s.bounced}</div>
                </div>
              </div>
            </div>
          ))}
          {steps.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-zinc-500">No drip emails sent in this range yet.</div>
          )}
        </div>

        {/* Backfill action */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <UserMinus className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5 rotate-180" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-amber-900 mb-1">One-time backfill</h3>
              <p className="text-xs text-amber-900 mb-3">
                Enrol every drip-eligible lead from the selected window who isn't already in the sequence and hasn't opted out. Use this once if you launched the drip after some leads already came in.
              </p>
              <button
                onClick={runBackfill}
                disabled={backfilling}
                data-testid="backfill-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-amber-300 text-amber-900 hover:bg-amber-100 rounded-lg text-xs font-semibold disabled:opacity-50"
              >
                {backfilling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Run backfill for last {days}d
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ icon, label, value, sub, bg, fg, testid }) {
  return (
    <div className="bg-white rounded-xl border p-4" data-testid={testid}>
      <div className={`inline-flex items-center justify-center w-7 h-7 rounded-md ${bg} ${fg} mb-2`}>
        {icon}
      </div>
      <div className="text-2xl font-bold text-zinc-900 leading-none">{value}</div>
      <div className="text-xs text-zinc-500 mt-1">{label}</div>
      {sub && <div className="text-[11px] text-zinc-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function FunnelStage({ label, value, sub, tone }) {
  const palette = {
    zinc: "bg-zinc-50 text-zinc-900",
    green: "bg-green-50 text-green-900",
    amber: "bg-amber-50 text-amber-900",
  }[tone] || "bg-zinc-50 text-zinc-900";
  return (
    <div className={`rounded-lg p-3 ${palette}`}>
      <div className="text-3xl font-bold leading-none">{value}</div>
      <div className="text-[11px] uppercase tracking-wide mt-1 opacity-70">{label}</div>
      {sub && <div className="text-[11px] mt-0.5 opacity-80">{sub}</div>}
    </div>
  );
}
